import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n, translateTimeline } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { fmtDate, STATUS_KEYS, docProtocol, sortDocumentsByProtocol, displayStatusForDocument, isRefusedDocument, documentMovementHistory, formatDocumentsSearchInput, formatProtocolInput, canonicalStatus } from "../lib/format";
import { formatTaxIdInput, formatZipCodeInput, digitsOnly } from "../lib/tax-id";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Search, SlidersHorizontal, MoreHorizontal, X, MapPin, Navigation, Gauge, Ruler, Plus, Upload, Download, Pencil, Truck, Camera, FileText, Paperclip, ChevronLeft, ChevronRight, Loader2, Radio } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { Link, useSearchParams } from "react-router-dom";
import { useLivePoll } from "../lib/live";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function parseCoords(item) {
  if (!item) return null;
  const lat = Number(item.lat);
  const lng = Number(item.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
}

function FitToPoints({ points, maxZoom = 15 }) {
  const map = useMap();
  const key = (points || []).map((p) => `${p?.[0]},${p?.[1]}`).join("|");
  useEffect(() => {
    const valid = (points || []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (!valid.length) return;
      if (valid.length === 1) map.setView(valid[0], Math.min(15, maxZoom));
      else map.fitBounds(L.latLngBounds(valid), { padding: [48, 48], maxZoom });
    }, 80);
    return () => clearTimeout(timer);
  }, [map, maxZoom, key]);
  return null;
}

function statusPill(status, t) {
  const key = canonicalStatus(status);
  const map = {
    delivered: "pill pill-success",
    returned: "pill pill-warning",
    canceled: "pill pill-danger",
    out_for_delivery: "pill pill-info",
    attempted: "pill pill-warning",
    received: "pill pill-neutral",
    sorting: "pill pill-info",
  };
  return { cls: map[key] || "pill pill-neutral", label: t(`status_${key}`) };
}

function progressPct(status) {
  if (status === "returned" || status === "canceled") return 100;
  const idx = STATUS_KEYS.indexOf(status);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / STATUS_KEYS.length) * 100);
}

function fmtTime(d, lang) {
  return d ? d.toLocaleTimeString(lang === "en" ? "en-US" : "pt-BR", { hour: "2-digit", minute: "2-digit" }) : "--:--";
}
function fmtDay(d, lang) {
  return d ? d.toLocaleDateString(lang === "en" ? "en-US" : "pt-BR", { day: "2-digit", month: "short" }) : "—";
}

// ─── Order card in the left list ──────────────────────────────────────
function OrderCard({ doc, selected, onSelect, onEdit, canEdit, canSelect, checked, onToggleCheck, delivererName, t, lang }) {
  const displayStatus = displayStatusForDocument(doc);
  const pill = statusPill(displayStatus, t);
  const movements = documentMovementHistory(doc);
  const pct = progressPct(displayStatus);

  return (
    <div
      onClick={() => onSelect(doc)}
      className={`cursor-pointer rounded-2xl p-4 transition-all ${
        selected
          ? "bg-[var(--brand)] text-white shadow-lg shadow-blue-500/25"
          : "bg-[var(--surface)] border border-[var(--border-c)] hover:border-[var(--brand)]/30"
      }`}
      data-testid={`document-row-${docProtocol(doc)}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {canSelect && (
            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
              <Checkbox
                checked={checked}
                onCheckedChange={() => onToggleCheck(doc.id)}
                aria-label={docProtocol(doc)}
                data-testid={`document-check-${doc.id}`}
                className={selected ? "border-white bg-transparent data-[state=checked]:bg-white data-[state=checked]:text-[var(--brand)]" : "bg-[var(--surface)]"}
              />
            </div>
          )}
          <div className={`text-sm font-semibold truncate ${selected ? "text-white" : "text-[var(--text-1)]"}`}>
            {t("order_id")}: <span className="font-mono">{docProtocol(doc)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={pill.cls}>{pill.label}</span>
          {isRefusedDocument(doc) && (
            <span className={`text-[10px] font-medium ${selected ? "text-white/80" : "text-[var(--text-2)]"}`}>Recusado</span>
          )}
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className={`p-1 rounded-lg hover:bg-black/10 ${selected ? "text-white/70 hover:text-white" : "text-[var(--text-3)] hover:text-[var(--text-1)]"}`}
                  data-testid={`document-menu-${doc.id}`}
                >
                  <MoreHorizontal size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => onEdit(doc)} data-testid={`document-edit-${doc.id}`}>
                  <Pencil size={14} className="mr-2" />
                  {t("doc_edit")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {canSelect && (
        <div className={`mt-2 flex items-center gap-1.5 text-xs ${selected ? "text-white/80" : "text-[var(--text-2)]"}`}>
          <Truck size={12} className="shrink-0" />
          <span className="truncate">{delivererName || t("so_deliverer_none")}</span>
        </div>
      )}

      {/* Progress bar */}
      <div className="mt-3 progress-track" style={selected ? { background: "rgba(255,255,255,0.25)" } : undefined}>
        <div className={selected ? "progress-fill-white" : "progress-fill"} style={{ width: `${pct}%` }} />
      </div>

      {/* Timeline */}
      <div className={`mt-4 grid grid-cols-[52px_1fr_auto] gap-x-3 gap-y-3 items-center text-sm ${selected ? "text-white" : "text-[var(--text-1)]"}`}>
        {movements.map((h, idx) => {
          const d = h.at ? new Date(h.at) : null;
          const current = idx === movements.length - 1;
          return (
            <React.Fragment key={`${h.status}-${h.at}-${idx}`}>
              <div className={`text-xs ${selected ? "text-white/70" : "text-[var(--text-2)]"}`}>{fmtDay(d, lang)}</div>
              <div className="flex items-center gap-3 relative">
                <span
                  className="timeline-dot flex-shrink-0"
                  style={{
                    borderColor: selected ? "#FFFFFF" : "var(--brand)",
                    background: selected ? "#FFFFFF" : "var(--brand)",
                  }}
                />
                {idx < movements.length - 1 && (
                  <span
                    className="absolute left-1 top-4 bottom-[-14px] w-0.5"
                    style={{ background: selected ? "rgba(255,255,255,0.35)" : "var(--border-c)" }}
                  />
                )}
                <span className={`text-sm ${current ? "font-semibold" : ""}`}>
                  {t(`status_${canonicalStatus(h.status)}`)}
                  {h.note ? ` · ${translateTimeline(t, "note", h.note)}` : ""}
                </span>
              </div>
              <div className={`text-xs font-mono ${selected ? "text-white/80" : "text-[var(--text-2)]"}`}>{fmtTime(d, lang)}</div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const EVIDENCE_KINDS = ["photo", "signature", "ar", "receipt", "document"];

async function openEvidenceFile(id) {
  const { data } = await api.get(`/evidences/${id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function DocumentAttachments({ doc, user, t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("photo");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    if (!doc?.id) return;
    setLoading(true);
    api.get(`/evidences?document_id=${doc.id}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [doc?.id]);

  useEffect(() => { load(); }, [load]);

  const canUpload =
    user?.role === "admin" ||
    user?.role === "operator" ||
    (user?.role === "deliverer" && doc.deliverer_id === user.id);

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const params = new URLSearchParams({ document_id: doc.id, kind });
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/evidences/upload?${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("attachments_upload_ok"));
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("attachments_upload_fail"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pt-5 space-y-4" data-testid="doc-attachments">
      {canUpload && (
        <div className="rounded-2xl border border-dashed border-[var(--border-c)] p-4 space-y-3">
          <div className="text-sm font-semibold text-[var(--text-1)]">{t("attachments_add")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">{t("attachments_kind")}</label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="attachment-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVIDENCE_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>{translateTimeline(t, "evidence", k)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">{t("attachments_file")}</label>
              <label className="mt-1 flex items-center justify-center gap-2 h-10 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] cursor-pointer hover:bg-[var(--surface-2)] text-xs font-medium">
                <Paperclip size={14} />
                {file ? file.name : t("attachments_file")}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="attachment-file"
                />
              </label>
            </div>
          </div>
          <Button
            type="button"
            onClick={onUpload}
            disabled={!file || uploading}
            className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold"
            data-testid="attachment-upload"
          >
            <Upload size={14} className="mr-1.5" />
            {uploading ? "..." : t("attachments_upload")}
          </Button>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--text-2)]">{t("timeline_loading")}</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-[var(--text-2)]">{t("attachments_empty")}</div>
      )}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto">
          {items.map((e) => {
            const isImg = e.content_type?.startsWith("image/");
            return (
              <div key={e.id} className="rounded-2xl border border-[var(--border-c)] overflow-hidden" data-testid={`doc-attachment-${e.id}`}>
                <button
                  type="button"
                  onClick={() => openEvidenceFile(e.id).catch(() => toast.error(t("attachments_upload_fail")))}
                  className="w-full aspect-square bg-gradient-to-br from-[var(--surface-2)] to-[var(--brand-soft)] flex items-center justify-center hover:opacity-90"
                >
                  {isImg ? <Camera size={32} className="text-[var(--brand)]" /> : <FileText size={32} className="text-[var(--brand)]" />}
                </button>
                <div className="p-2.5 text-xs space-y-1">
                  <span className="pill pill-info">{translateTimeline(t, "evidence", e.kind)}</span>
                  <div className="font-mono text-[10px] text-[var(--text-2)] truncate">{e.original_filename || "—"}</div>
                  <div className="text-[var(--text-2)]">{fmtDate(e.created_at)}</div>
                  {e.user_email && (
                    <div className="text-[var(--text-3)] truncate">{t("attachments_by")}: {e.user_email}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DriverTab({ doc, deliverer, deliverers, canManage, t, onUpdated }) {
  const [selectedId, setSelectedId] = React.useState(doc.deliverer_id || "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setSelectedId(doc.deliverer_id || "");
  }, [doc.id, doc.deliverer_id]);

  const onAssign = async (value) => {
    const delivererId = value === "__none__" ? null : value;
    setSelectedId(value === "__none__" ? "" : value);
    if (!canManage) return;
    setSaving(true);
    try {
      const payload = { deliverer_id: delivererId };
      const early = ["received", "sorting", "ready_for_route", "assigned"];
      if (delivererId && early.includes(doc.status)) payload.status = "out_for_delivery";
      await api.patch(`/documents/${doc.id}`, payload);
      toast.success(t("deliverer_assigned_ok"));
      onUpdated?.();
    } catch (err) {
      setSelectedId(doc.deliverer_id || "");
      toast.error(formatApiError(err?.response?.data?.detail) || t("deliverer_assign_fail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-5 space-y-4" data-testid="doc-driver-tab">
      {canManage && (
        <div>
          <label className="text-xs font-medium text-[var(--text-2)]">{t("docs_deliverer_link")}</label>
          <Select
            value={selectedId || "__none__"}
            onValueChange={onAssign}
            disabled={saving}
          >
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="doc-deliverer-select">
              <SelectValue placeholder={t("no_deliverer")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("so_deliverer_none")}</SelectItem>
              {deliverers.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[var(--brand)] to-[var(--brand-2)] flex items-center justify-center text-white text-xl font-bold">
          {(deliverer?.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-[var(--text-1)]">{deliverer?.name || t("no_deliverer")}</div>
          <div className="text-xs text-[var(--text-2)]">{deliverer?.email || "—"}</div>
          <div className="text-xs text-[var(--text-3)] capitalize mt-0.5">{deliverer?.role || ""}</div>
        </div>
      </div>
    </div>
  );
}

function liveAgeLabel(createdAt, t) {
  if (!createdAt) return t("live_now");
  const ageMs = Date.now() - new Date(createdAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs < 15_000) return t("live_now");
  if (ageMs < 60_000) return t("live_seconds_ago").replace("{n}", String(Math.max(1, Math.floor(ageMs / 1000))));
  return t("live_minutes_ago").replace("{n}", String(Math.max(1, Math.floor(ageMs / 60_000))));
}

function DocumentLiveMap({ doc, dest, delivererName, t }) {
  const [ping, setPing] = useState(null);
  const delivererId = doc.deliverer_id || "";

  const load = useCallback(async () => {
    try {
      const qs = delivererId ? `?deliverer_id=${encodeURIComponent(delivererId)}` : "";
      const { data } = await api.get(`/tracking/live${qs}`);
      const list = data?.deliverers || [];
      const match = delivererId
        ? list.find((p) => p.deliverer_id === delivererId)
        : null;
      setPing(match && parseCoords(match) ? match : null);
    } catch {
      /* o mapa do destino continua mesmo se o GPS falhar */
    }
  }, [delivererId]);

  useLivePoll(load, 8000, Boolean(doc?.id));

  const liveCoords = parseCoords(ping);
  const destCoords = dest;
  const points = [destCoords, liveCoords].filter(Boolean);
  const center = destCoords || liveCoords || [-15.7, -47.9];
  const name = ping?.deliverer_name || delivererName || t("tab_driver_info");
  const when = t("live_updated").replace("{when}", liveAgeLabel(ping?.created_at, t));

  return (
    <div className="leaflet-map-shell flex-1 min-h-[220px] sm:min-h-[260px] relative">
      <MapContainer
        key={doc.id}
        center={center}
        zoom={points.length ? 15 : 4}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
        <FitToPoints points={points} maxZoom={16} />
        {destCoords && (
          <Marker key={`dest:${destCoords.join(",")}`} position={destCoords}>
            <Tooltip direction="bottom" offset={[0, 8]}>
              {t("live_destination")}
            </Tooltip>
          </Marker>
        )}
        {liveCoords && destCoords && (
          <Polyline
            positions={[liveCoords, destCoords]}
            pathOptions={{ color: "#14B8A6", weight: 3, dashArray: "8 8", opacity: 0.85 }}
          />
        )}
        {liveCoords && (
          <CircleMarker
            center={liveCoords}
            radius={10}
            pathOptions={{ color: "#0D9488", fillColor: "#14B8A6", fillOpacity: 0.9 }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]}>
              {name}
            </Tooltip>
            <Popup>
              <div className="text-sm font-semibold">{name}</div>
              <div className="text-xs text-muted-foreground font-mono">
                {liveCoords[0].toFixed(5)}, {liveCoords[1].toFixed(5)}
                {ping.accuracy != null ? ` (±${Math.round(ping.accuracy)}m)` : ""}
              </div>
              <div className="text-xs mt-1">{when}</div>
            </Popup>
          </CircleMarker>
        )}
      </MapContainer>

      <Link
        to="/tracking"
        className="absolute top-3 right-3 z-[1100] inline-flex items-center gap-1 rounded-full bg-[var(--surface)]/95 border border-[var(--border-c)] px-2.5 py-1.5 text-[11px] font-medium text-[var(--text-2)] hover:text-[var(--brand)]"
      >
        <Radio size={12} />
        {t("live_open_tracking")}
      </Link>
      <div
        className="absolute bottom-3 left-3 z-[1100] flex items-center gap-2 rounded-full bg-[var(--surface)]/95 border border-[var(--border-c)] px-3 py-1.5 shadow-sm text-xs font-medium text-[var(--text-1)] max-w-[80%]"
        data-testid="doc-live-chip"
      >
        {liveCoords ? (
          <>
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
            </span>
            <span className="truncate">{name} · {t("live_on_map")} · {when}</span>
          </>
        ) : (
          <>
            <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--text-3)]" />
            <span className="text-[var(--text-2)]">{t("live_no_gps")}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Right panel: detail with map + tabs ──────────────────────────────
function DetailPanel({ doc, deliverers, user, canManage, onUpdated, t }) {
  if (!doc) {
    return (
      <div className="card-soft h-full flex items-center justify-center text-[var(--text-2)] text-sm p-6">
        {t("select_delivery")}
      </div>
    );
  }
  const coords = parseCoords(doc);
  const hasGps = coords != null;
  const deliverer = deliverers?.find((u) => u.id === doc.deliverer_id);

  return (
    <div className="card-soft h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-c)]">
        <div className="text-base font-semibold text-[var(--text-1)]">
          {t("order_id")}: <span className="font-mono">{docProtocol(doc)}</span>
        </div>
      </div>

      <DocumentLiveMap
        doc={doc}
        dest={hasGps ? coords : null}
        delivererName={deliverer?.name}
        t={t}
      />

      {/* Main info tabs */}
      <div className="px-5 pt-5 pb-5">
        <div className="text-lg font-semibold text-[var(--text-1)] mb-3">{t("main_info")}</div>
        <Tabs defaultValue="order">
          <TabsList className="w-full justify-start bg-transparent border-b border-[var(--border-c)] rounded-none h-auto p-0 gap-6">
            {[
              ["order", t("tab_order_details")],
              ["attachments", t("tab_attachments")],
              ["driver", t("tab_driver_info")],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand)] data-[state=active]:text-[var(--brand)] px-0 pb-3 pt-1 text-sm font-medium text-[var(--text-2)]"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="order" className="pt-5 space-y-3 text-sm">
            <InfoRow label={t("label_recipient")} value={doc.recipient_name} />
            <InfoRow label={t("label_address")} value={`${doc.address}, ${doc.city}`} />
            <InfoRow label={t("label_notary")} value={doc.notary_office} />
            <InfoRow label={t("label_created")} value={fmtDate(doc.created_at)} />
          </TabsContent>

          <TabsContent value="attachments">
            <DocumentAttachments doc={doc} user={user} t={t} />
          </TabsContent>

          <TabsContent value="driver">
            <DriverTab
              doc={doc}
              deliverer={deliverer}
              deliverers={deliverers}
              canManage={canManage}
              t={t}
              onUpdated={onUpdated}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5">
      <div className="text-xs text-[var(--text-2)] font-medium">{label}</div>
      <div className="col-span-2 text-sm text-[var(--text-1)]">{value || "—"}</div>
    </div>
  );
}

function InfoSmall({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-2)] font-medium">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

const PAGE_SIZE = 3;

// ─── Main page ─────────────────────────────────────────────────────────
export default function Documents() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [q, setQ] = useState(formatDocumentsSearchInput(params.get("q") || ""));
  const [statusFilter, setStatusFilter] = useState("all");
  const [delivererFilter, setDelivererFilter] = useState("all");
  const [checkedIds, setCheckedIds] = useState([]);
  const [linkDelivererId, setLinkDelivererId] = useState("");
  const [linking, setLinking] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const canManageDocs = user?.role === "admin" || user?.role === "operator";

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (statusFilter !== "all") qs.set("status", statusFilter);
    if (delivererFilter !== "all") qs.set("deliverer_id", delivererFilter);
    try {
      const { data } = await api.get(`/documents?${qs}`);
      setDocs(sortDocumentsByProtocol(data));
      setPage(1);
      setSelectedId((prev) => {
        if (!data.length) return null;
        if (prev && data.some((d) => d.id === prev)) return prev;
        return data[0].id;
      });
    } catch (e) {
      if (e?.response?.status === 401) return;
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao carregar documentos");
    }
  }, [q, statusFilter, delivererFilter]);

  useEffect(() => {
    void load();
  }, [statusFilter, delivererFilter, load]);

  useEffect(() => {
    const tm = setTimeout(() => { void load(); }, 300);
    return () => clearTimeout(tm);
  }, [q, load]);
  useEffect(() => {
    if (canManageDocs) {
      api.get("/users?role=deliverer").then((r) => setDeliverers(r.data || [])).catch(() => {});
    }
  }, [user, canManageDocs]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/documents/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`${data.created} documentos importados`);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(docs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageDocs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return docs.slice(start, start + PAGE_SIZE);
  }, [docs, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  useEffect(() => {
    if (!pageDocs.length) return;
    if (!selectedId || !pageDocs.some((d) => d.id === selectedId)) {
      setSelectedId(pageDocs[0].id);
    }
  }, [pageDocs, selectedId]);

  const selected = useMemo(() => docs.find((d) => d.id === selectedId), [docs, selectedId]);
  const delivererLabel = (id) => deliverers.find((d) => d.id === id)?.name || "";
  const pageAllChecked = pageDocs.length > 0 && pageDocs.every((d) => checkedIds.includes(d.id));
  const pageSomeChecked = pageDocs.some((d) => checkedIds.includes(d.id));
  const allFilteredChecked = docs.length > 0 && docs.every((d) => checkedIds.includes(d.id));

  useEffect(() => {
    const visible = new Set(docs.map((d) => d.id));
    setCheckedIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [docs]);

  const toggleOne = (id) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const togglePage = (on) => {
    const pageIds = pageDocs.map((d) => d.id);
    setCheckedIds((prev) => (on ? [...new Set([...prev, ...pageIds])] : prev.filter((id) => !pageIds.includes(id))));
  };

  const toggleFiltered = () => {
    setCheckedIds(allFilteredChecked ? [] : docs.map((d) => d.id));
  };

  const onLinkSelected = async () => {
    if (!checkedIds.length || !linkDelivererId) {
      toast.error(t("so_deliverer_select"));
      return;
    }
    setLinking(true);
    try {
      const delivererId = linkDelivererId === "__none__" ? null : linkDelivererId;
      const { data } = await api.post("/documents/assign-deliverer", {
        document_ids: checkedIds,
        deliverer_id: delivererId,
      });
      const name = delivererId ? (delivererLabel(delivererId) || data?.deliverer_name || "") : t("so_deliverer_none");
      toast.success(`${t("docs_link_ok")}: ${data?.updated ?? checkedIds.length} · ${name}`);
      setCheckedIds([]);
      setLinkDelivererId("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("deliverer_assign_fail"));
    } finally {
      setLinking(false);
    }
  };

  const activeFilters = [
    q && { key: "q", label: `"${q}"`, onClear: () => { setQ(""); setPage(1); } },
    statusFilter !== "all" && { key: "s", label: statusPill(statusFilter, t).label, onClear: () => { setStatusFilter("all"); setPage(1); } },
    delivererFilter !== "all" && {
      key: "d",
      label: delivererFilter === "__none__" ? t("so_deliverer_none") : (delivererLabel(delivererFilter) || t("docs_deliverer_link")),
      onClear: () => { setDelivererFilter("all"); setPage(1); },
    },
  ].filter(Boolean);

  const goToPage = (next) => {
    const safe = Math.min(Math.max(1, next), totalPages);
    setPage(safe);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:h-[calc(100vh-6rem)] min-h-0">
      {/* LEFT — Tracking list */}
      <div className="card-soft flex flex-col overflow-hidden min-h-[50vh] lg:min-h-0">
        <div className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 space-y-3 border-b border-[var(--border-c)]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--text-3)]">
                {t("nav_documents")}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[var(--text-1)] mt-0.5 leading-tight">
                {t("tracking_delivery")}
              </h1>
              <p className="text-xs text-[var(--text-2)] mt-1">
                {docs.length} {docs.length === 1 ? t("document_singular") : t("documents_count_label")}
                {docs.length > 0 && (
                  <span className="text-[var(--text-3)]">
                    {" · "}{t("pagination_page")} {currentPage}/{totalPages}
                  </span>
                )}
              </p>
            </div>
            {(user?.role === "admin" || user?.role === "operator") && (
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-[var(--surface-2)] text-xs font-semibold hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]" data-testid="import-csv-button">
                  <Upload size={13} /> CSV
                  <input type="file" accept=".csv" hidden onChange={onImport} />
                </label>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/documents.csv`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[var(--surface-2)] text-xs font-semibold hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]" data-testid="export-button" aria-label="Export CSV">
                  <Download size={14} />
                </a>
                <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="new-document-button">
                  <Plus size={14} className="mr-1" /> {t("new_short")}
                </Button>
              </div>
            )}
          </div>

          {/* Search + filter chip */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative min-w-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
              <Input
                value={q}
                onChange={(e) => setQ(formatDocumentsSearchInput(e.target.value))}
                placeholder={t("search_placeholder")}
                className="pl-9 h-11 rounded-2xl border-[var(--border-c)] bg-[var(--surface)] focus-visible:ring-[var(--brand)]"
                data-testid="documents-search"
                inputMode="search"
                autoComplete="off"
              />
            </div>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="h-11 w-11 shrink-0 rounded-2xl bg-[var(--surface-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] flex items-center justify-center"
              data-testid="filter-btn"
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>

          {canManageDocs && (
            <div className="space-y-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-3)]" htmlFor="documents-deliverer-filter">
                {t("docs_deliverer_link")}
              </label>
              <Select
                value={delivererFilter}
                onValueChange={(value) => { setDelivererFilter(value); setPage(1); }}
              >
                <SelectTrigger id="documents-deliverer-filter" className="h-11 rounded-2xl" data-testid="documents-deliverer-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("docs_deliverer_all")}</SelectItem>
                  <SelectItem value="__none__">{t("so_deliverer_none")}</SelectItem>
                  {deliverers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Filter chips */}
        {(activeFilters.length > 0 || showFilters) && (
          <div className="px-5 mt-3 flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <button key={f.key} onClick={f.onClear} className="pill pill-neutral hover:opacity-80 flex items-center gap-1">
                {f.label} <X size={11} />
              </button>
            ))}
            {showFilters && (
              <div className="flex flex-wrap gap-1.5">
                {STATUS_KEYS.map((s) => {
                  const p = statusPill(s, t);
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => { setStatusFilter(active ? "all" : s); setPage(1); }}
                      className={`${p.cls} ${active ? "ring-2 ring-offset-2 ring-[var(--brand)]" : "opacity-70 hover:opacity-100"}`}
                      data-testid={`filter-status-${s}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {canManageDocs && docs.length > 0 && (
          <div className="px-4 sm:px-5 py-3 border-b border-[var(--border-c)] space-y-3 shrink-0" data-testid="documents-link-bar">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--text-2)]">
                <Checkbox
                  checked={pageAllChecked ? true : pageSomeChecked ? "indeterminate" : false}
                  onCheckedChange={(value) => togglePage(value === true)}
                  aria-label={t("docs_select_page")}
                  data-testid="documents-select-page"
                />
                {t("docs_select_page")}
              </label>
              <button
                type="button"
                onClick={toggleFiltered}
                className="text-xs font-semibold text-[var(--brand)] hover:underline"
                data-testid="documents-select-filtered"
              >
                {allFilteredChecked ? t("docs_clear_selection") : t("docs_select_filtered")}
              </button>
              {checkedIds.length > 0 && (
                <span className="text-xs text-[var(--text-3)]">
                  {t("docs_selected_count").replace("{n}", String(checkedIds.length))}
                </span>
              )}
            </div>
            {checkedIds.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1 min-w-0 space-y-1">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-3)]">
                    {t("docs_deliverer_link")}
                  </label>
                  <Select value={linkDelivererId || undefined} onValueChange={setLinkDelivererId} disabled={linking}>
                    <SelectTrigger className="h-10 rounded-xl" data-testid="documents-link-deliverer">
                      <SelectValue placeholder={t("so_deliverer_select")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("so_deliverer_none")}</SelectItem>
                      {deliverers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={onLinkSelected}
                  disabled={linking || !linkDelivererId}
                  className="h-10 rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-xs font-semibold"
                  data-testid="documents-link-selected"
                >
                  {linking ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Truck size={14} className="mr-1" />}
                  {t("docs_link_selected")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Card list — 3 per page */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {docs.length === 0 && <div className="text-center py-10 text-[var(--text-2)] text-sm">{t("no_documents")}</div>}
          {pageDocs.map((d) => (
            <OrderCard
              key={d.id}
              doc={d}
              selected={selectedId === d.id}
              onSelect={(x) => setSelectedId(x.id)}
              onEdit={setEditDoc}
              canEdit={canManageDocs}
              canSelect={canManageDocs}
              checked={checkedIds.includes(d.id)}
              onToggleCheck={toggleOne}
              delivererName={d.deliverer_id ? delivererLabel(d.deliverer_id) : ""}
              t={t}
              lang={lang}
            />
          ))}
        </div>

        {docs.length > 0 && (
          <div className="px-4 sm:px-5 py-3 border-t border-[var(--border-c)] flex items-center justify-between gap-2 shrink-0">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-xl bg-[var(--surface-2)] text-xs font-semibold text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] disabled:opacity-40 disabled:pointer-events-none"
              data-testid="documents-page-prev"
            >
              <ChevronLeft size={14} /> {t("pagination_prev")}
            </button>
            <div className="text-xs font-semibold text-[var(--text-2)] tabular-nums text-center" data-testid="documents-page-indicator">
              {t("pagination_page")} {currentPage} {t("pagination_of")} {totalPages}
              <span className="block sm:inline text-[var(--text-3)] font-medium sm:before:content-['·_']">
                {t("pagination_per_page").replace("{n}", String(PAGE_SIZE))}
              </span>
            </div>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-xl bg-[var(--surface-2)] text-xs font-semibold text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] disabled:opacity-40 disabled:pointer-events-none"
              data-testid="documents-page-next"
            >
              {t("pagination_next")} <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* RIGHT — detail */}
      <div className="hidden lg:block h-full min-h-0 overflow-y-auto">
        <DetailPanel doc={selected} deliverers={deliverers} user={user} canManage={canManageDocs} onUpdated={load} t={t} />
      </div>

      {/* Mobile: detail as bottom sheet */}
      {selected && (
        <div className="lg:hidden">
          <DetailPanel doc={selected} deliverers={deliverers} user={user} canManage={canManageDocs} onUpdated={load} t={t} />
        </div>
      )}

      <DocumentFormDialog mode="create" open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <DocumentFormDialog mode="edit" doc={editDoc} open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)} onSaved={load} />
    </div>
  );
}

const EMPTY_CREATE_FORM = {
  protocol_number: "",
  recipient_name: "",
  tax_id: "",
  address: "",
  neighborhood: "",
  city: "",
  state: "",
  zip_code: "",
  lat: null,
  lng: null,
  tenant_id: "",
  notary_office: "",
};

function docToForm(doc, tenants) {
  const tn = tenants.find((x) => x.id === doc.tenant_id) || tenants.find((x) => x.name === doc.notary_office);
  return {
    protocol_number: formatProtocolInput(doc.protocol_number || doc.number || ""),
    recipient_name: doc.recipient_name || "",
    tax_id: doc.recipient_cpf || doc.recipient_cnpj || "",
    address: doc.address || "",
    neighborhood: doc.neighborhood || "",
    city: doc.city || "",
    state: doc.state || "",
    zip_code: doc.zip_code || "",
    lat: doc.lat ?? null,
    lng: doc.lng ?? null,
    tenant_id: doc.tenant_id || tn?.id || "",
    notary_office: doc.notary_office || tn?.name || "",
  };
}

function DocumentFormDialog({ mode, doc, open, onOpenChange, onSaved }) {
  const isEdit = mode === "edit";
  const { t } = useI18n();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [tenants, setTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const lastLookedUpCep = useRef("");
  const cepAbort = useRef(null);

  useEffect(() => {
    if (!open) {
      cepAbort.current?.abort();
      setLookingUpCep(false);
      return;
    }
    lastLookedUpCep.current = "";
    api.get("/tenants")
      .then((r) => {
        const list = r.data || [];
        setTenants(list);
        if (isEdit && doc) {
          setForm(docToForm(doc, list));
        } else if (list.length === 1) {
          setForm({ ...EMPTY_CREATE_FORM, tenant_id: list[0].id, notary_office: list[0].name });
        } else if (user?.tenant_id) {
          const userTenantId = Array.isArray(user.tenant_id) ? user.tenant_id[0] : user.tenant_id;
          const tn = list.find((x) => x.id === userTenantId);
          setForm({ ...EMPTY_CREATE_FORM, tenant_id: userTenantId, notary_office: tn?.name || "" });
        } else {
          setForm(EMPTY_CREATE_FORM);
        }
      })
      .catch(() => setTenants([]));
  }, [open, isEdit, doc, user?.tenant_id]);

  const lookupCep = async (cep) => {
    const digits = digitsOnly(cep);
    if (digits.length !== 8 || digits === lastLookedUpCep.current) return;

    lastLookedUpCep.current = digits;
    cepAbort.current?.abort();
    const controller = new AbortController();
    cepAbort.current = controller;
    setLookingUpCep(true);

    try {
      const { data } = await api.get(`/cep/${digits}`, { signal: controller.signal });
      setForm((f) => ({
        ...f,
        zip_code: formatZipCodeInput(data.zip_code || digits),
        address: data.address || f.address,
        neighborhood: data.neighborhood || f.neighborhood,
        city: data.city || f.city,
        state: data.state || f.state,
        lat: data.lat ?? null,
        lng: data.lng ?? null,
      }));
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      lastLookedUpCep.current = "";
      toast.error(formatApiError(err?.response?.data?.detail) || t("cep_lookup_fail"));
    } finally {
      if (cepAbort.current === controller) setLookingUpCep(false);
    }
  };

  const setField = (key, raw) => {
    let value = raw;
    if (key === "tax_id") value = formatTaxIdInput(raw);
    if (key === "protocol_number") value = formatProtocolInput(raw);
    if (key === "zip_code") value = formatZipCodeInput(raw);
    if (key === "state") value = raw.toUpperCase().slice(0, 2);
    if (key === "zip_code" && digitsOnly(value).length !== 8) {
      setForm((f) => ({ ...f, zip_code: value, lat: null, lng: null }));
      return;
    }
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "zip_code" && digitsOnly(value).length === 8) {
      void lookupCep(value);
    }
  };

  const onTenantChange = (tenantId) => {
    const tn = tenants.find((x) => x.id === tenantId);
    setForm((f) => ({ ...f, tenant_id: tenantId, notary_office: tn?.name || "" }));
  };

  const payload = {
    protocol_number: form.protocol_number,
    recipient_name: form.recipient_name,
    tax_id: form.tax_id,
    address: form.address,
    neighborhood: form.neighborhood || undefined,
    city: form.city,
    state: form.state || undefined,
    zip_code: form.zip_code || undefined,
    lat: form.lat,
    lng: form.lng,
    notary_office: form.notary_office,
    tenant_id: form.tenant_id,
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tenant_id) {
      toast.error(t("dlg_select_notary"));
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/documents/${doc.id}`, payload);
        toast.success(t("doc_updated"));
      } else {
        await api.post("/documents", payload);
        toast.success(t("doc_created"));
      }
      onOpenChange(false);
      setForm(EMPTY_CREATE_FORM);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (isEdit ? t("doc_update_failed") : t("doc_create_failed")));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["protocol_number", t("dlg_protocol_number"), "text"],
    ["recipient_name", t("dlg_debtor"), "text"],
    ["tax_id", t("dlg_tax_id"), "text"],
    ["zip_code", t("dlg_zip"), "text", { inputMode: "numeric" }],
    ["address", t("dlg_address"), "text"],
    ["neighborhood", t("dlg_neighborhood"), "text"],
    ["city", t("dlg_city"), "text"],
    ["state", t("dlg_state"), "text", { maxLength: 2 }],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-document-dialog" : "create-document-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold">{isEdit ? t("dlg_edit_document") : t("dlg_new_document")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">{t("dlg_notary")}</label>
            <Select value={form.tenant_id} onValueChange={onTenantChange} disabled={tenants.length <= 1 && !!form.tenant_id}>
              <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="create-input-notary">
                <SelectValue placeholder={t("dlg_select_notary")} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((tn) => (
                  <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fields.map(([k, label, type, extra]) => (
            <div key={k}>
              <label className="text-xs font-medium text-[var(--text-2)] inline-flex items-center gap-1.5">
                {label}
                {k === "zip_code" && lookingUpCep && (
                  <Loader2 size={12} className="animate-spin text-[var(--brand)]" data-testid="cep-lookup-spinner" />
                )}
              </label>
              <Input
                type={type}
                value={form[k]}
                onChange={(e) => setField(k, e.target.value)}
                className="rounded-xl h-10 mt-1"
                data-testid={`${isEdit ? "edit" : "create"}-input-${k}`}
                {...(extra || {})}
              />
              {k === "zip_code" && form.lat != null && form.lng != null && (
                <div className="text-[11px] text-[var(--text-3)] mt-1" data-testid="cep-gps-coords">
                  GPS {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                </div>
              )}
            </div>
          ))}
          <Button
            type="submit"
            disabled={saving || !form.protocol_number || !form.recipient_name || !form.address || !form.city || !form.tenant_id}
            className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11"
            data-testid={isEdit ? "edit-document-submit" : "create-document-submit"}
          >
            {saving ? "..." : (isEdit ? t("dlg_save") : t("dlg_create"))}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
